import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Row, Col, Card, Table, Badge } from 'react-bootstrap';
import AuthContext from '../context/AuthContext';

const OrganizationDashboard = () => {
  const { orgId } = useParams();
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`/api/organizations/${orgId}/consolidated`);
        setData(res.data);
      } catch (err) {
        console.error('Error fetching consolidated data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId]);

  if (loading) return <div className="p-5 text-center"><i className="fas fa-spinner fa-spin fa-2x"></i></div>;
  if (!data) return <div className="p-5 text-center">Organization not found.</div>;

  return (
    <div className="container-fluid py-4">
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <div>
            <nav aria-label="breadcrumb">
                <ol className="breadcrumb bg-transparent p-0 mb-1">
                    <li className="breadcrumb-item"><Link to="/orgs">Organizations</Link></li>
                    <li className="breadcrumb-item active">Dashboard</li>
                </ol>
            </nav>
            <h1 className="h3 font-weight-bold">Organization Insights</h1>
        </div>
      </div>

      {/* High Level Totals */}
      <Row className="mb-4">
        <Col md={3}>
            <Card className="border-0 shadow-sm bg-primary text-white">
                <Card.Body>
                    <div className="small text-white-50 text-uppercase font-weight-bold mb-1">Total Revenue</div>
                    <div className="h4 mb-0">${data.totals.income.toLocaleString()}</div>
                </Card.Body>
            </Card>
        </Col>
        <Col md={3}>
            <Card className="border-0 shadow-sm bg-success text-white">
                <Card.Body>
                    <div className="small text-white-50 text-uppercase font-weight-bold mb-1">Combined Profit</div>
                    <div className="h4 mb-0">${data.totals.profit.toLocaleString()}</div>
                </Card.Body>
            </Card>
        </Col>
        <Col md={3}>
            <Card className="border-0 shadow-sm bg-info text-white">
                <Card.Body>
                    <div className="small text-white-50 text-uppercase font-weight-bold mb-1">Total Assets</div>
                    <div className="h4 mb-0">${data.totals.assets.toLocaleString()}</div>
                </Card.Body>
            </Card>
        </Col>
        <Col md={3}>
            <Card className="border-0 shadow-sm bg-warning text-white">
                <Card.Body>
                    <div className="small text-white-50 text-uppercase font-weight-bold mb-1">Total Liabilities</div>
                    <div className="h4 mb-0">${data.totals.liabilities.toLocaleString()}</div>
                </Card.Body>
            </Card>
        </Col>
      </Row>

      {/* Side by Side Comparison */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-white py-3">
            <h5 className="mb-0 font-weight-bold text-dark">Entity Comparison</h5>
        </Card.Header>
        <Card.Body className="p-0">
            <Table hover responsive className="mb-0">
                <thead className="bg-light">
                    <tr>
                        <th className="border-0">Company Name</th>
                        <th className="border-0 text-right">Revenue</th>
                        <th className="border-0 text-right">Expenses</th>
                        <th className="border-0 text-right">Net Profit</th>
                        <th className="border-0 text-right">Assets</th>
                        <th className="border-0 text-right">Liabilities</th>
                    </tr>
                </thead>
                <tbody>
                    {data.byCompany.map((co, idx) => (
                        <tr key={idx}>
                            <td className="font-weight-bold">{co.name}</td>
                            <td className="text-right text-success">${co.income.toLocaleString()}</td>
                            <td className="text-right text-danger">${co.expenses.toLocaleString()}</td>
                            <td className={`text-right font-weight-bold ${co.profit >= 0 ? 'text-success' : 'text-danger'}`}>
                                ${co.profit.toLocaleString()}
                            </td>
                            <td className="text-right">${co.assets.toLocaleString()}</td>
                            <td className="text-right text-muted">${co.liabilities.toLocaleString()}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot className="bg-light font-weight-bold">
                    <tr>
                        <td>COMBINED TOTAL</td>
                        <td className="text-right">${data.totals.income.toLocaleString()}</td>
                        <td className="text-right">${data.totals.expenses.toLocaleString()}</td>
                        <td className="text-right">${data.totals.profit.toLocaleString()}</td>
                        <td className="text-right">${data.totals.assets.toLocaleString()}</td>
                        <td className="text-right">${data.totals.liabilities.toLocaleString()}</td>
                    </tr>
                </tfoot>
            </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default OrganizationDashboard;
